import {
  Box,
  Flex,
  HStack,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Text,
  Tooltip,
  useToast,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { MdOutlineSmartToy } from 'react-icons/md';
import { IoCopyOutline } from 'react-icons/io5';
import { HelivanColor } from '../helivan/HelivanColor';

const SKILL_PROMPT =
  'Sign yourself up on AgentWatch by following the instructions at agentwatch.helivan.io/skill.md';

interface ConnectAgentModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// Lightweight "connect your agent" modal for signed-out visitors. No account or
// connector code needed: the agent follows skill.md, which registers it, creates
// a workspace, and emails the operator to finish signing up.
export const ConnectAgentModal = ({ isOpen, onClose }: ConnectAgentModalProps) => {
  const toast = useToast();
  const navigate = useNavigate();

  const copyPrompt = () => {
    navigator.clipboard.writeText(SKILL_PROMPT);
    toast({ title: 'Copied', description: 'Paste it to your agent.', status: 'success', duration: 2000 });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered>
      <ModalOverlay bg="blackAlpha.500" backdropFilter="blur(6px)" />
      <ModalContent bg="white" borderRadius="16px">
        <Flex px={6} pt={6} pb={2} align="center" gap={3}>
          <Flex w="40px" h="40px" borderRadius="12px" bg={HelivanColor.hover.lightBlue} align="center" justify="center">
            <MdOutlineSmartToy size={20} color={HelivanColor.text.helivanBlue} />
          </Flex>
          <Box>
            <Text fontSize="18px" fontWeight={700} color={HelivanColor.text.blueDark}>
              Connect your agent
            </Text>
            <Text fontSize="13px" color={HelivanColor.text.blueMid}>
              No account needed to start.
            </Text>
          </Box>
        </Flex>
        <ModalCloseButton top={4} right={4} />
        <ModalBody px={6} pb={6}>
          <Text fontSize="14px" color={HelivanColor.text.blueMid} lineHeight="1.6" mb={3}>
            Paste this to your AI agent. It signs itself up, creates your workspace,
            and emails you to finish setting up your account.
          </Text>
          <Flex bg="#1a1a2e" borderRadius="12px" p={3} align="flex-start" justify="space-between" gap={2}>
            <Text fontFamily="mono" fontSize="13px" color="#00e5cc" lineHeight="1.6" wordBreak="break-word" flex={1}>
              {SKILL_PROMPT}
            </Text>
            <Tooltip label="Copy">
              <IconButton
                aria-label="Copy prompt"
                icon={<IoCopyOutline />}
                size="sm"
                variant="ghost"
                color="white"
                _hover={{ bg: 'whiteAlpha.200' }}
                onClick={copyPrompt}
                flexShrink={0}
              />
            </Tooltip>
          </Flex>
          <HStack mt={4} justify="flex-end" spacing={3}>
            <Text
              as="button"
              fontSize="13px"
              fontWeight={600}
              color={HelivanColor.text.helivanBlue}
              _hover={{ textDecoration: 'underline' }}
              onClick={() => navigate('/login')}
            >
              Already have an account? Sign in
            </Text>
            <Box
              as="button"
              px={5}
              py={2.5}
              borderRadius="10px"
              fontSize="14px"
              fontWeight={600}
              color="white"
              bg={HelivanColor.text.helivanBlue}
              _hover={{ bg: HelivanColor.text.blueDark }}
              onClick={copyPrompt}
            >
              Copy prompt
            </Box>
          </HStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
